import axios from 'axios';
import { getToken } from '../utils/token';

const BASE = `${process.env.REACT_APP_URL_PATH}/api/journal`;

const auth = () => ({
  headers: {
    Authorization: getToken(),
    'Content-Type': 'application/json;charset=utf-8',
  },
});

// ─── Группы ───────────────────────────────────────────────────────────────────

export const getGroups = (filters = {}) => {
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined && value !== null)
  ).toString();

  return axios
    .get(`${BASE}/groups${query ? `?${query}` : ''}`, auth())
    .then((resp) => resp.data);
};

/** Учебные годы, которые есть в базе, плюс текущий и следующий. */
export const getAcademicYears = () =>
  axios.get(`${BASE}/academic-years`, auth()).then((resp) => resp.data);

/** Поиск слушателя по ФИО: отдаёт группу и куратора, не трогая тяжёлый /api/student. */
export const searchStudents = (query) =>
  axios.get(`${BASE}/students/search?q=${encodeURIComponent(query)}`, auth())
    .then((resp) => resp.data);

export const getGroup = (id) =>
  axios.get(`${BASE}/groups/${id}`, auth()).then((resp) => resp.data);

export const createGroup = (payload) =>
  axios.post(`${BASE}/groups`, payload, auth()).then((resp) => resp.data);

export const updateGroup = (id, payload) =>
  axios.put(`${BASE}/groups/${id}`, payload, auth()).then((resp) => resp.data);

// ─── Состав ───────────────────────────────────────────────────────────────────

export const addStudentToGroup = (groupId, payload) =>
  axios.post(`${BASE}/groups/${groupId}/students`, payload, auth()).then((resp) => resp.data);

export const transferStudent = (membershipId, payload) =>
  axios.post(`${BASE}/memberships/${membershipId}/transfer`, payload, auth()).then((resp) => resp.data);

export const expelStudent = (membershipId, payload) =>
  axios.post(`${BASE}/memberships/${membershipId}/expel`, payload, auth()).then((resp) => resp.data);

/** Правка периода обучения: дата зачисления и выбытия. */
export const updateMembership = (membershipId, payload) =>
  axios.patch(`${BASE}/memberships/${membershipId}`, payload, auth()).then((resp) => resp.data);

/**
 * Кого можно добавить в группу. Отбор и поиск — на сервере: весь контингент это
 * 3800 слушателей и 11 МБ в одном ответе.
 */
export const getGroupCandidates = (groupId, query = '') =>
  axios.get(
    `${BASE}/groups/${groupId}/candidates${query ? `?query=${encodeURIComponent(query)}` : ''}`,
    auth(),
  ).then((resp) => resp.data);

// ─── Преподаватели группы ─────────────────────────────────────────────────────

/** Кого можно назначить: те, кто может вести журнал и ещё не в составе группы. */
export const getTeacherCandidates = (groupId) =>
  axios.get(`${BASE}/groups/${groupId}/teacher-candidates`, auth()).then((resp) => resp.data);

export const assignTeacher = (groupId, payload) =>
  axios.post(`${BASE}/groups/${groupId}/teachers`, payload, auth()).then((resp) => resp.data);

export const updateTeacher = (groupId, linkId, payload) =>
  axios.patch(`${BASE}/groups/${groupId}/teachers/${linkId}`, payload, auth()).then((resp) => resp.data);

/** Снятие с группы закрывает связь датой — история выработки на неё опирается. */
export const closeTeacher = (groupId, linkId, date) =>
  axios.delete(
    `${BASE}/groups/${groupId}/teachers/${linkId}${date ? `?date=${date}` : ''}`,
    auth(),
  ).then((resp) => resp.data);

// ─── Лист журнала ─────────────────────────────────────────────────────────────

export const getSheet = (groupId, month) =>
  axios.get(`${BASE}/groups/${groupId}/sheet?month=${month}`, auth()).then((resp) => resp.data);

export const createLesson = (groupId, payload) =>
  axios.post(`${BASE}/groups/${groupId}/lessons`, payload, auth()).then((resp) => resp.data);

export const updateLesson = (groupId, lessonId, payload) =>
  axios.put(`${BASE}/groups/${groupId}/lessons/${lessonId}`, payload, auth()).then((resp) => resp.data);

/** force=true удаляет занятие вместе с отметками — интерфейс сначала переспрашивает. */
export const deleteLesson = (groupId, lessonId, force = false) =>
  axios.delete(`${BASE}/groups/${groupId}/lessons/${lessonId}${force ? '?force=true' : ''}`, auth())
    .then((resp) => resp.data);

/** Отметки уходят пачкой: преподаватель заполняет колонку за день целиком. */
export const saveMarks = (groupId, marks) =>
  axios.put(`${BASE}/groups/${groupId}/marks`, { marks }, auth()).then((resp) => resp.data);

export const deleteMark = (groupId, lessonId, studentId) =>
  axios.delete(`${BASE}/groups/${groupId}/lessons/${lessonId}/marks/${studentId}`, auth())
    .then((resp) => resp.data);

/** Свой цвет преподавателя — применяется ко всем его действующим группам. */
export const setMyColor = (color) =>
  axios.put(`${BASE}/my/color`, { color }, auth()).then((resp) => resp.data);

// ─── Комментарии и аудит ──────────────────────────────────────────────────────

export const getStudentComments = (studentId, kind) =>
  axios.get(`${BASE}/students/${studentId}/comments${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`, auth())
    .then((resp) => resp.data);

export const createStudentComment = (studentId, payload) =>
  axios.post(`${BASE}/students/${studentId}/comments`, payload, auth()).then((resp) => resp.data);

export const deleteStudentComment = (commentId) =>
  axios.delete(`${BASE}/comments/${commentId}`, auth()).then((resp) => resp.data);

/** История правок отметки: кто и когда менял часы или причину отсутствия. */
export const getMarkHistory = (groupId, lessonId, studentId) =>
  axios.get(`${BASE}/groups/${groupId}/lessons/${lessonId}/marks/${studentId}/history`, auth())
    .then((resp) => resp.data);

// ─── Файлы слушателя ──────────────────────────────────────────────────────────

export const getStudentFiles = (studentId) =>
  axios.get(`${BASE}/students/${studentId}/files`, auth()).then((resp) => resp.data);

export const addFileLink = (studentId, payload) =>
  axios.post(`${BASE}/students/${studentId}/files/link`, payload, auth()).then((resp) => resp.data);

export const getFileDownloadUrl = (fileId) =>
  axios.get(`${BASE}/files/${fileId}/download`, auth()).then((resp) => resp.data);

export const deleteFile = (fileId) =>
  axios.delete(`${BASE}/files/${fileId}`, auth()).then((resp) => resp.data);

/**
 * Загрузка файла в три шага: сервер выдаёт подписанную ссылку, браузер кладёт файл
 * напрямую в бакет, сервер подтверждает и проверяет объект.
 *
 * Файл не проходит через приложение: на сервере одно ядро, и проксировать байты через
 * Node — значит занять единственный поток на всё время загрузки.
 */
export const uploadStudentFile = async (studentId, file, meta = {}) => {
  const { data } = await axios.post(
    `${BASE}/students/${studentId}/files/upload-url`,
    { name: file.name, mime: file.type, size: file.size, ...meta },
    auth(),
  );

  // Прямой PUT в S3 — без заголовка Authorization: подпись уже в самой ссылке.
  await axios.put(data.upload_url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });

  const confirmed = await axios.put(`${BASE}/files/${data.file_id}/confirm`, {}, auth());
  return confirmed.data;
};

// ─── Уведомления о пропусках ──────────────────────────────────────────────────

export const getAlerts = (status) =>
  axios.get(`${BASE}/alerts${status ? `?status=${encodeURIComponent(status)}` : ''}`, auth())
    .then((resp) => resp.data);

export const getAlertsCount = () =>
  axios.get(`${BASE}/alerts/count`, auth()).then((resp) => resp.data);

export const markAlertViewed = (alertId) =>
  axios.put(`${BASE}/alerts/${alertId}/viewed`, {}, auth()).then((resp) => resp.data);

// ─── Отчёты ───────────────────────────────────────────────────────────────────

/** Выработка часов за месяц. Преподаватель получает только свою. */
export const getWorkload = (month, teacherId) =>
  axios.get(`${BASE}/reports/workload?month=${month}${teacherId ? `&teacher_id=${teacherId}` : ''}`, auth())
    .then((resp) => resp.data);

// ─── Слушатель ────────────────────────────────────────────────────────────────

export const getStudentStats = (studentId) =>
  axios.get(`${BASE}/students/${studentId}/stats`, auth()).then((resp) => resp.data);

export const getStudentHistory = (studentId) =>
  axios.get(`${BASE}/students/${studentId}/history`, auth()).then((resp) => resp.data);
